import React, { useEffect, useRef, useState } from 'react';
import { QRCodeCanvas, QRCodeSVG } from 'qrcode.react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { getTelegramWebApp } from '@/hooks/useTelegram';
import { copyText } from '@/utils/clipboard';
import { buildInviteLinkUrl, buildTelegramShareUrl, buildTelegramStartAppUrl } from '@/utils/inviteLink';
import styles from './styles.module.scss';

interface InviteLinkShareProps {
  token: string;
  /** Used for testids so several instances can coexist in a list. */
  linkId: number;
}

const QR_DISPLAY_SIZE = 176;
// High-resolution canvas for the downloadable PNG (print-friendly).
const QR_EXPORT_SIZE = 1024;
const COPIED_FEEDBACK_MS = 2000;

const CopyIcon: React.FC = () => (
  <svg className={styles.svgIcon} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="8.5" y="8.5" width="11" height="11" rx="2" stroke="currentColor" strokeWidth="1.8" />
    <path d="M15.5 5.5V5a1.5 1.5 0 0 0-1.5-1.5H6A1.5 1.5 0 0 0 4.5 5v8A1.5 1.5 0 0 0 6 14.5h.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const DownloadIcon: React.FC = () => (
  <svg className={styles.svgIcon} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 4v11m0 0-4.5-4.5M12 15l4.5-4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M4.5 16.5v1.5A1.5 1.5 0 0 0 6 19.5h12a1.5 1.5 0 0 0 1.5-1.5v-1.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

/**
 * Invite link output: URL field with an inline Copy button, QR code, PNG download and share.
 * Share opens the Telegram share sheet inside the Mini App (with the Mini App `startapp` link
 * when configured, else the web URL); outside Telegram it uses the Web Share API, falling back
 * to the t.me share page in a new tab. PNG download is hidden inside Telegram, whose WebView
 * ignores `<a download>`.
 */
export const InviteLinkShare: React.FC<InviteLinkShareProps> = ({ token, linkId }) => {
  const { t } = useTranslation('tournament');
  // Read synchronously so the Telegram-specific buttons don't flicker on first render.
  const telegramWebApp = getTelegramWebApp();
  const isInTelegram = telegramWebApp !== null;
  const exportCanvasRef = useRef<HTMLCanvasElement>(null);
  // The 1024px export canvas is mounted only while a download is in progress.
  const [isExporting, setIsExporting] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  const url = buildInviteLinkUrl(token);
  const shareText = t('inviteLink.shareText');
  const downloadLabel = t('inviteLink.downloadPng');
  const shareLabel = t('inviteLink.share');
  const copyLabel = isCopied ? t('inviteLink.copied') : t('inviteLink.copy');

  const handleCopy = async () => {
    const copied = await copyText(url);
    if (copied) {
      setIsCopied(true);
    } else {
      toast.error(t('inviteLink.copyFailed'));
    }
  };

  useEffect(() => {
    if (!isCopied) return undefined;
    const timer = window.setTimeout(() => setIsCopied(false), COPIED_FEEDBACK_MS);
    return () => window.clearTimeout(timer);
  }, [isCopied]);

  const handleShare = async () => {
    if (telegramWebApp) {
      const sharedUrl = buildTelegramStartAppUrl(token) ?? url;
      telegramWebApp.openTelegramLink(buildTelegramShareUrl(sharedUrl, shareText));
      return;
    }
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ title: shareText, text: shareText, url });
      } catch (error) {
        // AbortError = user closed the share sheet; anything else is worth a hint.
        if ((error as DOMException)?.name !== 'AbortError') {
          toast.error(t('inviteLink.shareFailed'));
        }
      }
      return;
    }
    window.open(buildTelegramShareUrl(url, shareText), '_blank', 'noopener,noreferrer');
  };

  // Child effects (QRCodeCanvas drawing) run before this one, so the canvas is ready here.
  useEffect(() => {
    if (!isExporting) return;
    const canvas = exportCanvasRef.current;
    if (canvas) {
      const anchor = document.createElement('a');
      anchor.href = canvas.toDataURL('image/png');
      anchor.download = `pclash-invite-${linkId}.png`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
    }
    setIsExporting(false);
  }, [isExporting, linkId]);

  return (
    <div className={styles.share} data-testid={`invite-link-share-${linkId}`}>
      <div className={styles.urlField}>
        <p className={styles.url} data-testid={`invite-link-url-${linkId}`}>
          {url}
        </p>
        <button
          type="button"
          className={`btn btn--sm btn--outlined ${styles.copyBtn}`}
          data-copied={isCopied}
          onClick={handleCopy}
          aria-label={copyLabel}
          title={copyLabel}
          data-testid={`invite-link-copy-${linkId}`}
        >
          {isCopied ? <i className="icon-check" aria-hidden="true" /> : <CopyIcon />}
          <span className={styles.copiedText} aria-live="polite">
            {isCopied ? t('inviteLink.copied') : ''}
          </span>
        </button>
      </div>

      <div className={styles.qrRow}>
        <div className={styles.qrBox}>
          <QRCodeSVG
            value={url}
            size={QR_DISPLAY_SIZE}
            level="M"
            marginSize={2}
            title={t('inviteLink.qrAlt')}
            role="img"
            aria-label={t('inviteLink.qrAlt')}
            data-testid={`invite-link-qr-${linkId}`}
          />
        </div>
        {isExporting && (
          <QRCodeCanvas
            ref={exportCanvasRef}
            value={url}
            size={QR_EXPORT_SIZE}
            level="M"
            marginSize={4}
            className={styles.exportCanvas}
            aria-hidden="true"
          />
        )}

        <div className={styles.qrActions}>
          {!isInTelegram && (
            <button
              type="button"
              className={`btn btn--sm btn--outlined ${styles.iconBtn}`}
              onClick={() => setIsExporting(true)}
              disabled={isExporting}
              aria-label={downloadLabel}
              title={downloadLabel}
              data-testid={`invite-link-download-${linkId}`}
            >
              <DownloadIcon />
            </button>
          )}
          <button
            type="button"
            className={`btn btn--sm btn--outlined ${styles.iconBtn}`}
            onClick={handleShare}
            aria-label={shareLabel}
            title={shareLabel}
            data-testid={`invite-link-share-btn-${linkId}`}
          >
            <i className="icon-share" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default InviteLinkShare;
